import React, { useState, useEffect, useRef } from "react"
import DatoCmsSearch from "datocms-search/dist/datocms-search.base"
import { useDebounce } from "use-debounce"
import styled from "styled-components"
import { gsap } from "gsap"

import Input from "@atoms/input"
import { Section, Container, Flex, Column } from "@atoms/layout"
import { Heading } from "@atoms/typography"
import BlogCategories from "@molecules/blog/categories"
import CardSearch from "@molecules/cards/search"
import { media } from "@styles/breakpoints"
import {
  CSSTransition,
  TransitionGroup,
} from 'react-transition-group';

const client = new DatoCmsSearch("08d3dba3bb1a49ae96da05c4c24a3c")

const NavBlog = () => {
  // DATOCMS SEARCH SETUP
  const [nav, setNav] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [results, setResults] = useState([])
  const [isSearching, setIsSearching] = useState(false)
  const [debouncedSearchTerm] = useDebounce(searchTerm, 500)

  const formPreventDefault = event => {
    event.preventDefault()
  }

  const search = async query => {
    const [{ results: blog }] = await Promise.all([client.search(query, { limit: 9 })])
    return [].concat(blog)
  }

  useEffect(() => {
    if (debouncedSearchTerm) {
      setIsSearching(true)
      setNav(true)
      
      search(debouncedSearchTerm).then(results => {
        setIsSearching(false)
        setResults(results)
      })
      
    } else {
      setNav(false)
      setResults([])
    }

    !nav && (
      document.body.setAttribute("style", "overflow: auto; max-height: auto;")
      );
    nav && (
      document.body.setAttribute("style", "overflow: hidden; max-height: 100vh;")
    );

    return () => {
       document.body.setAttribute("style", "overflow: auto; max-height: auto;")
    }
  }, [debouncedSearchTerm, nav])

  // MOBILE LAYOUT & INTERACTION
  const searchColumn = useRef()
  const searchInput = useRef()
  const [mobileSearch, setMobileSearch] = useState(false)
  const [tl] = useState(gsap.timeline({ paused: true }))

  const toggleMobile = () => {
    if (window.innerWidth < 1200) {
      setMobileSearch(!mobileSearch)
    }
  }

  useEffect(() => {
    tl.addLabel("start")
    tl.set(
      searchInput.current,
      {
        opacity: 0,
        duration: 0,
      },
      "start"
    )
    tl.to(
      ".blogCategories",
      {
        zIndex: "-2",
        opacity: "0",
        padding: "0",
        duration: 0,
      },
      "start"
    )
    tl.to(
      ".blogCategories",
      {
        flex: "0 0 0%",
        width: "0px",
      },
      "start"
    )
    tl.to(
      searchColumn.current,
      {
        flex: "0 0 100%",
        duration: 0.5,
      },
      "start"
    )
    tl.to(
      searchInput.current,
      {
        opacity: 1,
        duration: 0.5,
      },
      "start"
    ).reverse()
  }, [tl])

  useEffect(() => {
    tl.reversed(!mobileSearch)
  }, [tl, mobileSearch])

  return (
    <Nav light>
      <Container>
        <Flex alignCenter>
          <BlogCategories />
          <Search className="blogSearch" ref={searchColumn}>
            <SearchForm onSubmit={formPreventDefault}>
              <SearchInput
                white
                sm
                name="query"
                type="search"
                placeholder="Search"
                ref={searchInput}
                value={searchTerm}
                className="searchInput"
                onChange={e => setSearchTerm(e.target.value)}
              />
              <SearchIcon
                onClick={toggleMobile}
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 18 18"
              >
                <path d="M7.333 14c1.479 0 2.915-.495 4.08-1.407l3.664 3.664 1.178-1.179-3.663-3.663a6.627 6.627 0 001.407-4.082A6.674 6.674 0 007.333.667 6.674 6.674 0 00.666 7.333 6.674 6.674 0 007.333 14zm0-11.667c2.757 0 5 2.243 5 5 0 2.758-2.243 5-5 5-2.758 0-5-2.242-5-5 0-2.757 2.242-5 5-5z"></path>
              </SearchIcon>
            </SearchForm>
          </Search>
        </Flex>
      </Container>
      <Container>
        <SearchResults open={searchTerm && true}>
          <SearchTitle>
            <Heading html="h4" level="h4">
              Search Results: {isSearching ? "searching" : `${results.length}`}
            </Heading>
          </SearchTitle>
          {searchTerm && (
            <TransitionGroup component={FlexResults} open={searchTerm && true}>
              {results.map((result, index) => {
                return (
                  <CSSTransition
                    key={index}
                    timeout={500}
                    classNames="item"
                  >        
                    <CardSearch result={result} />
                  </CSSTransition>
                )
              })}
            </TransitionGroup>
          )}
        </SearchResults>
      </Container>
    </Nav>
  )
}

export default NavBlog

const Nav = styled(Section)`
  border-bottom: var(--borderPrimary);
  left: 0;
  position: sticky;
  top: var(--spacingNavigation);
  z-index: var(--zFront);
`

const Search = styled(Column)`
  flex: 1 1 50%;
  transition: var(--transitionBase);
  margin: 0;
`

const SearchForm = styled.form`
  align-items: center;
  display: flex;
  justify-content: flex-end;
  margin: 1rem 0;
`

const SearchIcon = styled.svg`
  background-color: white;
  border-radius: 5rem;
  height: 2.25rem;
  padding: 0.5rem;
  position: absolute;
  right: 0.675rem;
  width: 2.25rem;
  z-index: var(--zTest);

  ${media.sm`
    right: 2rem;
  `}

  path {
    width: 100%;
    height: 100%;
    fill: var(--gray--100);
    transition: var(--transitionBase);
  }
`

const SearchTitle = styled.div`
  padding: var(--spacing--xs) 0;
`

const SearchInput = styled(Input)`
  border: 1px solid var(--gray--20);
  opacity: 0;
  transition: var(--transitionBase);

  ${media.sm`
    opacity: 1;
    width: 400px;
  `}

  &:active ~ ${SearchIcon}, &:focus ~ ${SearchIcon} {
    path {
      fill: var(--aqua--60);
    }
  }
`

const SearchResults = styled.div`
  overflow: scroll;
  overflow-x: hidden; 
  min-height: ${props => (props.open ? "calc(100vh - 10rem)" : "0")};
  max-height: ${props => (props.open ? "calc(100vh - 10rem)" : "0")};
  transition: var(--transitionLong);
`

const FlexResults = styled(Flex)`
  margin-bottom: ${props => (props.open ? "5rem" : "0")};

  .item-enter {
    opacity: 0;
    transform: translateY(1rem);
  }
  .item-enter-active {
    opacity: 1;
    transform: translateY(0);
    transition: all 500ms ease-in;
  }
  .item-exit {
    transform: translateY(0);
    opacity: 1;
  }
  .item-exit-active {
    transform: translateY(1rem);
    opacity: 0;
    transition: all 500ms ease-out;
  }

`
